/** Solver cell state — numeric for fast arithmetic in solver loops */
export enum CellValue {
  Empty = -1,
  Unknown = 0,
  Filled = 1,
}

/** Clues for a full puzzle: row clues and column clues */
export interface PuzzleClues {
  rows: number[][];
  cols: number[][];
}

/** Structured error describing why a cell should be empty */
export type CellError =
  | { type: 'row_empty'; row: number }
  | { type: 'col_empty'; col: number }
  | { type: 'row_full'; row: number; clueTotal: number }
  | { type: 'col_full'; col: number; clueTotal: number }
  | { type: 'row_no_group'; row: number }
  | { type: 'col_no_group'; col: number }
  | { type: 'row_gap'; row: number }
  | { type: 'col_gap'; col: number };

/** Result of validating a single cell placement */
export interface CellValidationResult {
  valid: boolean;
  errors: CellError[];
  message: string | null;
}

/** Optional metadata attached to a puzzle */
export interface PuzzleMetadata {
  name?: string;
  author?: string;
  source?: string;
}

/** A nonogram puzzle definition (no solution included) */
export interface Puzzle {
  clues: PuzzleClues;
  rows: number;
  cols: number;
  metadata?: PuzzleMetadata;
}

/** A single deduction made by the solver */
export interface SolveStep {
  technique: Technique;
  lineType: 'row' | 'col';
  lineIndex: number;
  cellsDeduced: Array<{ row: number; col: number; value: CellValue }>;
}

/** Technique names used by the solver */
export type Technique =
  | 'trivialLine'
  | 'simpleOverlap'
  | 'edgeLogic'
  | 'spaceFilling'
  | 'backtracking';

/** Result of solving a puzzle */
export interface SolveResult {
  solved: boolean;
  grid: CellValue[][];
  steps: SolveStep[];
  techniquesUsed: Set<Technique>;
  usedBacktracking: boolean;
  /** Number of alternating h/v sweeps needed to solve (0 if unsolvable by line-logic) */
  sweepCount: number;
  /** True if line-logic alone couldn't solve and backtracking was required */
  requiresBacktracking: boolean;
}

/**
 * Difficulty rating derived from solve analysis.
 * @deprecated Use `SolveResult.sweepCount` directly.
 */
export type DifficultyRating =
  | 'trivial'
  | 'easy'
  | 'medium'
  | 'hard'
  | 'expert';

/** Options for puzzle generation */
export interface GenerateOptions {
  rows: number;
  cols: number;
  density?: number;
  seed?: number | string;
  maxDifficulty?: DifficultyRating;
  maxAttempts?: number;
  /**
   * Maximum backtracking states to explore while proving uniqueness.
   * If the budget is exhausted, the candidate is rejected as "unknown"
   * rather than being accepted without proof.
   */
  uniquenessMaxStates?: number;
  /**
   * Use the constructive Adapt algorithm (Batenburg et al. 2009) to
   * guarantee a line-solvable puzzle. Instead of filtering random grids,
   * iteratively flips white→black pixels until the puzzle is solvable
   * by line-logic alone. Always succeeds on the first attempt.
   */
  constructive?: boolean;
}

/** Result of puzzle generation */
export interface GenerateResult {
  puzzle: Puzzle;
  solution: boolean[][];
  seed: number;
  difficulty: DifficultyRating;
  attempts: number;
  /** Completed proof that the returned clues have exactly one solution. */
  uniqueness: ProvenUniqueSolutionAnalysis;
}

export interface SolutionAnalysisOptions {
  /** Stop searching after this many solutions. Default: 2 */
  limit?: number;
  /**
   * Maximum backtracking states to explore.
   * If omitted, the search is exact.
   */
  maxStates?: number;
}

export interface SolutionAnalysisResult {
  /** Number of solutions found, capped by `limit` */
  count: number;
  /** True when the search completed exactly within the provided budget */
  complete: boolean;
  /** True when the search stopped because the budget was exhausted */
  aborted: boolean;
  /** Number of branch states explored during search */
  exploredStates: number;
}

export interface ProvenUniqueSolutionAnalysis extends SolutionAnalysisResult {
  count: 1;
  complete: true;
  aborted: false;
}

/**
 * Sweep-count based difficulty rating (Batenburg et al. 2009).
 * @deprecated Use `SolveResult.sweepCount` directly.
 */
export type SweepDifficultyRating =
  | 'trivial'
  | 'easy'
  | 'medium'
  | 'hard'
  | 'expert';

/** Options for the Adapt algorithm (Batenburg et al. 2009) */
export interface AdaptOptions {
  /** Weight for minimizing remaining unknowns (default: 8) */
  alpha?: number;
  /** Weight for resemblance to source image — lower P[i,j] = darker = preferred (default: 1) */
  beta?: number;
  /** Source image grayscale values (0-255), used with beta. If not provided, beta is ignored */
  sourceImage?: number[][];
  /** Previously generated puzzles for diversity weighting */
  existingPuzzles?: boolean[][][];
  /** Weight for diversity from existing puzzles (default: 8) */
  gamma?: number;
}

/** Options for the Vary algorithm (Batenburg et al. 2009) */
export interface VaryOptions extends AdaptOptions {
  /** Maximum number of pixel additions to try (default: grid area / 2) */
  depth?: number;
}

/** A single variant produced by the Vary algorithm */
export interface VaryResult {
  /** The puzzle grid */
  grid: boolean[][];
  /** Sweep count difficulty */
  sweepCount: number;
  /** Clues for this variant */
  clues: PuzzleClues;
}

/** Result of the FullSettle operation */
export interface FullSettleResult {
  /** The partially-solved grid */
  grid: CellValue[][];
  /** Number of cells still unknown */
  remainingUnknowns: number;
  /** Total number of alternating sweeps performed */
  sweepCount: number;
}

/** Options for converting a grayscale image to a nonogram */
export interface FromImageOptions {
  /** Target fill ratio (0-1). Default: 0.35. Ignored if threshold is set. */
  targetFillRatio?: number;
  /** Manual threshold (0-255). Pixels below this are filled. Overrides targetFillRatio. */
  threshold?: number;
  /** Ensure puzzle is line-solvable (no backtracking) via pixel toggling. Default: true */
  ensureSolvable?: boolean;
  /** Weight for minimizing unknowns during adaptation (default: 8) */
  alpha?: number;
  /** Weight for source image resemblance during adaptation (default: 1) */
  beta?: number;
}

/** Result of converting a grayscale image to a nonogram */
export interface FromImageResult {
  /** The final boolean grid (puzzle solution) */
  grid: boolean[][];
  /** The puzzle object (clues + dimensions) */
  puzzle: Puzzle;
  /** Threshold value used for initial conversion */
  threshold: number;
  /** Number of solver sweeps needed */
  sweepCount: number;
  /** Whether backtracking is required to solve */
  requiresBacktracking: boolean;
  /** Fill ratio of the final grid */
  fillRatio: number;
  /** Whether the grid was adapted (pixels toggled) to ensure solvability */
  adapted: boolean;
  /** Number of pixels toggled during adaptation */
  pixelsToggled: number;
}

/** Serialized puzzle for import/export */
export interface SerializedPuzzle {
  version: 1;
  rows: number;
  cols: number;
  clues: { rows: number[][]; cols: number[][] };
  metadata?: PuzzleMetadata;
}
